import * as Sentry from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { uniq } from 'lodash';

import API from '@api/API';

import { useCurrentUserGagglRankQuery } from '@queries/CritiqueQueries';
import {
  Entertainer,
  EntertainerFeaturedCta,
  EntertainerType,
  useCityEntertainerAvailabilityQuery,
  useCurrentEntertainerBadgesQuery,
  useCurrentEntertainerQuery,
  useCurrentEntertainerScheduleQuery,
  useCurrentUserEntertainerCritiqueQuery,
  useEntertainerReviewsQuery,
} from '@queries/EntertainerQueries';
import { useSeriesEntertainersQuery } from '@queries/TagQueries';

export interface CreateEntertainerEventMutationPayload {
  eventImage: any;
  title: string;
  description: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  cityId: string;
  isRecurring: boolean;
  entertainerIds: string[];
  tagIds: string[];
  recurringSchedule?: {
    type: 'weekly' | 'bi-weekly' | 'monthlyNthDay';
  };
  venue: {
    _id?: string;
    title: string;
  };
}

export const useCreateEntertainerEventMutation = (entertainerId: string) => {
  const queryClient = useQueryClient();

  const { isPending: isSubmitting, mutateAsync } = useMutation({
    mutationFn: (payload: CreateEntertainerEventMutationPayload) => {
      return API.post<{}, CreateEntertainerEventMutationPayload>(
        `/v1/user/entertainer/${entertainerId}/events`,
        payload,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: useCurrentEntertainerQuery.queryKey(),
      });
    },
  });

  return {
    isSubmitting,
    createEntertainerEventAsync: mutateAsync,
  };
};

interface AddEntertainerToEventMutationPayload {
  eventId: string;
  referredBy?: string;
}
export const useAddEntertainerToEventMutation = () => {
  const queryClient = useQueryClient();

  const { isPending: isSubmitting, mutateAsync } = useMutation({
    mutationFn: ({
      entertainerId,
      ...payload
    }: AddEntertainerToEventMutationPayload & { entertainerId: string }) => {
      return API.post<{}, AddEntertainerToEventMutationPayload>(
        `/v1/user/entertainer/${entertainerId}/add-to-event`,
        payload,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: useCurrentEntertainerQuery.queryKey(),
      });
    },
  });

  return {
    isSubmitting,
    addEntertainerToEventAsync: mutateAsync,
  };
};

interface RespondToEntertainerEventRequestMutationPayload {
  eventId: string;
  status: 'accepted' | 'rejected';
}
export const useRespondToEntertainerEventRequestMutation = (entertainerId: string) => {
  const queryClient = useQueryClient();

  const { isPending: isSubmitting, mutateAsync } = useMutation({
    mutationFn: (payload: RespondToEntertainerEventRequestMutationPayload) => {
      return API.post<{}, RespondToEntertainerEventRequestMutationPayload>(
        `/v1/user/entertainer/${entertainerId}/respond-to-event`,
        payload,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: useCurrentEntertainerQuery.queryKey(),
      });
    },
  });

  return {
    isSubmitting,
    respondToEntertainerEventRequestAsync: mutateAsync,
  };
};

export type UpdateEntertainerEventMutationPayload = Partial<CreateEntertainerEventMutationPayload>;

export const useUpdateEntertainerEventMutation = (entertainerId: string) => {
  const queryClient = useQueryClient();

  const { isPending: isSubmitting, mutateAsync } = useMutation({
    mutationFn: ({
      eventId,
      ...payload
    }: UpdateEntertainerEventMutationPayload & { eventId: string }) => {
      return API.put<{}, UpdateEntertainerEventMutationPayload>(
        `/v1/user/entertainer/${entertainerId}/events/${eventId}`,
        payload,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: useCurrentEntertainerQuery.queryKey(),
      });
    },
  });

  return {
    isSubmitting,
    updateEntertainerEventAsync: mutateAsync,
  };
};

export const useDeleteEntertainerEventMutation = (entertainerId: string) => {
  const queryClient = useQueryClient();

  const { isPending: isSubmitting, mutateAsync } = useMutation({
    mutationFn: ({ eventId }: { eventId: string }) => {
      return API.delete<{}, {}>(`/v1/user/entertainer/${entertainerId}/events/${eventId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: useCurrentEntertainerQuery.queryKey(),
      });
    },
  });

  return {
    isSubmitting,
    deleteEntertainerEventAsync: mutateAsync,
  };
};

export interface RespondToBookingRequestMutationPayload {
  status: 'interested' | 'notInterested';
}

export const useRespondToBookingRequestMutation = () => {
  const queryClient = useQueryClient();

  const { isPending: isSubmitting, mutateAsync } = useMutation({
    mutationFn: ({
      bookingRequestId,
      ...payload
    }: RespondToBookingRequestMutationPayload & { bookingRequestId: string }) => {
      return API.post<{}, RespondToBookingRequestMutationPayload>(
        `/v1/entertainer/booking-request/${bookingRequestId}/respond`,
        payload,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: useCurrentEntertainerQuery.queryKey(),
      });
    },
  });

  return {
    isSubmitting,
    respondToBookingRequestAsync: mutateAsync,
  };
};

export interface RespondToBookingOpportunityMutationPayload {
  status: 'interested' | 'notInterested';
}

export const useRespondToBookingOpportunityMutation = () => {
  const queryClient = useQueryClient();

  const { isPending: isSubmitting, mutateAsync } = useMutation({
    mutationFn: useRespondToBookingOpportunityMutation.mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: useCurrentEntertainerQuery.queryKey(),
      });
    },
  });

  return {
    isSubmitting,
    respondToBookingOpportunityAsync: mutateAsync,
  };
};
useRespondToBookingOpportunityMutation.mutationFn = ({
  bookingOpportunityId,
  ...payload
}: RespondToBookingOpportunityMutationPayload & { bookingOpportunityId: string }) => {
  return API.post<{}, RespondToBookingRequestMutationPayload>(
    `/v1/entertainer/booking-opportunity/${bookingOpportunityId}/respond`,
    payload,
  );
};

export interface RespondToBookingOpportunityNoAuthMutationPayload {
  title: string;
  instagram: string;
  status: 'interested' | 'notInterested';
}

export const useRespondToBookingOpportunityNoAuthMutation = () => {
  const queryClient = useQueryClient();

  const { isPending: isSubmitting, mutateAsync } = useMutation({
    mutationFn: useRespondToBookingOpportunityNoAuthMutation.mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: useCurrentEntertainerQuery.queryKey(),
      });
    },
  });

  return {
    isSubmitting,
    respondToBookingOpportunityNoAuthAsync: mutateAsync,
  };
};
useRespondToBookingOpportunityNoAuthMutation.mutationFn = ({
  bookingOpportunityId,
  ...payload
}: RespondToBookingOpportunityNoAuthMutationPayload & { bookingOpportunityId: string }) => {
  return API.post<{}, RespondToBookingOpportunityNoAuthMutationPayload>(
    `/v1/booking-opportunity/${bookingOpportunityId}/respond`,
    payload,
  );
};

interface CreateEntertainerReviewPayload {
  text: string;
  mediaIds?: string[];
  meta?: Record<string, unknown>;
}
export const useCreateEntertainerReview = (entertainerId: string) => {
  const queryClient = useQueryClient();

  const { isPending, mutateAsync } = useMutation({
    mutationFn: (payload: CreateEntertainerReviewPayload) => {
      return API.post(`/v1/entertainers/${entertainerId}/reviews/`, payload);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: useEntertainerReviewsQuery.queryKey(entertainerId),
      });
      queryClient.invalidateQueries({ queryKey: useCurrentUserGagglRankQuery.queryKey() });
    },
  });

  return {
    isSubmitting: isPending,
    createEntertainerReviewAsync: mutateAsync,
  };
};

interface SendEntertainerBadgePayload {
  lockedBadgeId?: string;
  eventId?: string;
  entertainerMediaId?: string;
  badgeId?: string;
  reason: string;
  meta?: Record<string, any>;
}
export const useSendEntertainerBadge = () => {
  const { entertainer: currentUserEntertainer } = useCurrentEntertainerQuery();
  const queryClient = useQueryClient();

  const { isPending, mutateAsync } = useMutation({
    mutationFn: ({
      entertainerId,
      ...payload
    }: SendEntertainerBadgePayload & { entertainerId: string }) => {
      return API.post(`/v1/entertainers/${entertainerId}/badges`, payload);
    },
    onSettled: (data, error, variables) => {
      const { entertainerId } = variables;
      queryClient.invalidateQueries({
        queryKey: useCurrentUserEntertainerCritiqueQuery.queryKey(entertainerId),
      });
      if (currentUserEntertainer?._id) {
        queryClient.invalidateQueries({
          queryKey: useCurrentEntertainerBadgesQuery.queryKey(currentUserEntertainer?._id),
        });
      }
    },
  });

  return {
    isSubmitting: isPending,
    sendEntertainerBadgeAsync: mutateAsync,
  };
};

interface SendBulkEntertainerBadgePayload {
  badges: (SendEntertainerBadgePayload & { entertainerId: string })[];
}

export const useSendBulkEntertainerBadges = () => {
  const { entertainer: currentUserEntertainer } = useCurrentEntertainerQuery();
  const queryClient = useQueryClient();

  const { isPending, mutateAsync } = useMutation({
    mutationFn: (payload: SendBulkEntertainerBadgePayload) => {
      return API.post(`/v1/entertainers/badges`, payload);
    },
    onSettled: (data, error, variables) => {
      const { badges } = variables;
      const entertainerIds = uniq(badges.map((badge) => badge.entertainerId));

      Promise.all(
        entertainerIds.map((entertainerId) =>
          queryClient.invalidateQueries({
            queryKey: useCurrentUserEntertainerCritiqueQuery.queryKey(entertainerId),
          }),
        ),
      );

      if (currentUserEntertainer?._id) {
        queryClient.invalidateQueries({
          queryKey: useCurrentEntertainerBadgesQuery.queryKey(currentUserEntertainer?._id),
        });
      }
    },
  });

  return {
    isSubmitting: isPending,
    sendBulkEntertainerBadgesAsync: mutateAsync,
  };
};

export interface AddEntertainerMediaPayload {
  title: string;
  description?: string;
  data: any;
  type: 'video' | 'image';
  mediaId?: string;
  meta?: {
    channel?: string;
    channelAction?: string;
  };
}

export const useAddEntertainerMediaMutation = () => {
  const queryClient = useQueryClient();

  const { isPending: isSubmitting, mutateAsync } = useMutation({
    mutationFn: ({
      entertainerId,
      ...payload
    }: AddEntertainerMediaPayload & { entertainerId: string }) => {
      return API.post<{}, AddEntertainerMediaPayload>(
        `/v1/user/entertainer/${entertainerId}/media`,
        payload,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: useCurrentEntertainerQuery.queryKey(),
      });
    },
  });

  return {
    isSubmitting,
    addEntertainerMediaAsync: mutateAsync,
  };
};

interface UpdateEntertainerAvailabilityPayload {
  cityIds?: string[];
  availability: Record<string, boolean>;
  hasFlexibleAvailability?: boolean;
  referredBy?: string;
}

export const useUpdateEntertainerAvailabilityMutation = () => {
  const queryClient = useQueryClient();

  const { isPending: isSubmitting, mutateAsync } = useMutation({
    mutationFn: (payload: UpdateEntertainerAvailabilityPayload) => {
      return API.post<{}, UpdateEntertainerAvailabilityPayload>(
        `/v1/user/entertainer/availability`,
        payload,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['city-entertainer-availability'],
        exact: false,
      });
    },
  });

  return {
    isSubmitting,
    updateEntertainerAvailabilityAsync: mutateAsync,
  };
};

interface UpdateEntertainerSchedulePayload {
  timezone: string;
  dates: string[];
}

export const useUpdateEntertainerScheduleMutation = () => {
  const { entertainer: currentEntertainer } = useCurrentEntertainerQuery();
  const queryClient = useQueryClient();

  const { isPending: isSubmitting, mutateAsync } = useMutation({
    mutationFn: (payload: UpdateEntertainerSchedulePayload) => {
      return API.post<{}, UpdateEntertainerSchedulePayload>(
        `/v1/user/entertainer/schedule`,
        payload,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: useCurrentEntertainerScheduleQuery.queryKey(currentEntertainer?._id ?? ''),
      });
    },
  });

  return {
    isSubmitting,
    updateEntertainerScheduleAsync: mutateAsync,
  };
};

interface UpdateEntertainerScheduleDateDetailsPayload {
  date: string;
  showName: string;
  venue: string;
}

export const useUpdateEntertainerScheduleDateDetailsMutation = () => {
  const { entertainer: currentEntertainer } = useCurrentEntertainerQuery();
  const queryClient = useQueryClient();

  const { isPending: isSubmitting, mutateAsync } = useMutation({
    mutationFn: (payload: UpdateEntertainerScheduleDateDetailsPayload) => {
      return API.post<{}, UpdateEntertainerScheduleDateDetailsPayload>(
        `/v1/user/entertainer/schedule/date-details`,
        payload,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: useCurrentEntertainerScheduleQuery.queryKey(currentEntertainer?._id ?? ''),
      });
    },
  });

  return {
    isSubmitting,
    updateEntertainerScheduleDateDetailsAsync: mutateAsync,
  };
};

export const useEntertainerJoinSeriesMutation = () => {
  const queryClient = useQueryClient();
  const { isPending: isSubmitting, mutateAsync } = useMutation({
    mutationFn: ({ seriesId }: { seriesId: string }) => {
      return API.post(`/v1/series/${seriesId}/entertainers/join`, {});
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: useSeriesEntertainersQuery.queryKey(variables.seriesId),
        exact: true,
      });
    },
  });

  return {
    isSubmitting,
    entertainerJoinSeriesAsync: mutateAsync,
  };
};

interface ClaimEntertainerByInstagramHandlePayload {
  phoneNumber: string;
  title?: string;
  cityId?: string;
  categories?: string[];
  entertainerType?: string;
}
export const useClaimEntertainerByInstagramHandleMutation = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: useClaimEntertainerByInstagramHandleMutation.mutationFn,
  });

  return {
    isClaimingEntertainerByInstagramHandle: isPending,
    claimEntertainerByInstagramHandleAsync: mutateAsync,
  };
};

useClaimEntertainerByInstagramHandleMutation.mutationFn = ({
  instagram,
  ...payload
}: ClaimEntertainerByInstagramHandlePayload & { instagram: string }) => {
  return API.post(`/v1/entertainer/instagram/${instagram}/claim`, payload);
};

export interface CreateEntertainerPayload {
  avatar: any;
  type: EntertainerType;
  title: string;
  tagline: string;
  description: string;
  cityIds: string[];
  contact?: Entertainer['contact'];
  categories?: string[];
}

export type UpdateEntertainerPayload = Partial<CreateEntertainerPayload> & {
  bookingHandle?: string;
  smsKeyword?: string;
  featuredCtas?: EntertainerFeaturedCta[];
};

export const useCreateEntertainerMutation = () => {
  const queryClient = useQueryClient();

  const { isPending: isSubmitting, mutateAsync: createEntertainerAsync } = useMutation({
    mutationFn: async ({
      avatar,
      title,
      tagline,
      description,
      type,
      contact,
      cityIds,
      categories,
    }: CreateEntertainerPayload) => {
      try {
        await API.post('/v1/entertainer/me', {
          type,
          title,
          tagline,
          description,
          contact,
          cityIds,
          categories,
        });

        await API.put('/v1/entertainer/me', {
          avatar,
        });
      } catch (e) {
        console.error(e);
        Sentry.captureException(e);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: useCurrentEntertainerQuery.queryKey() });
    },
  });

  return {
    isSubmitting,
    createEntertainerAsync,
  };
};

export const useUpdateEntertainerMutation = (options?: {
  invalidateQueriesOnSuccess?: boolean;
}) => {
  const { invalidateQueriesOnSuccess = true } = options ?? {};
  const queryClient = useQueryClient();

  const { isPending: isSubmitting, mutateAsync: updateEntertainerAsync } = useMutation({
    mutationFn: async ({
      avatar,
      title,
      type,
      tagline,
      description,
      contact,
      cityIds,
      bookingHandle,
      categories,
      smsKeyword,
      featuredCtas,
    }: UpdateEntertainerPayload) => {
      const promises: any[] = [];

      try {
        const promise = API.put('/v1/entertainer/me', {
          title,
          type,
          tagline,
          description,
          contact,
          cityIds,
          bookingHandle,
          categories,
          smsKeyword,
          featuredCtas,
        });

        promises.push(promise);
      } catch (e) {
        console.error(e);
        Sentry.captureException(e);
      }

      if (avatar) {
        try {
          const promise = API.put('/v1/entertainer/me', {
            avatar,
          });

          promises.push(promise);
        } catch (e) {
          console.error(e);
          Sentry.captureException(e);
        }
      }

      return Promise.all(promises);
    },
    onSuccess: (data, variables) => {
      const { bookingHandle } = variables;
      if (invalidateQueriesOnSuccess) {
        queryClient.invalidateQueries({ queryKey: ['user', 'entertainer'] });
        queryClient.invalidateQueries({
          queryKey: ['entertainer', 'bookingHandle', bookingHandle],
        });
      }
    },
  });

  return {
    isSubmitting,
    updateEntertainerAsync,
  };
};

export const useRemoveEntertainerPrimaryCtaMutation = () => {
  const queryClient = useQueryClient();

  const { isPending: isSubmitting, mutateAsync } = useMutation({
    mutationFn: ({ index }: { index: number; cta: EntertainerFeaturedCta }) => {
      return API.delete<{}, { index: number }>('/v1/entertainer/me/featured-cta', { index });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: useCurrentEntertainerQuery.queryKey() });
    },
  });

  return {
    isSubmitting,
    removeEntertainerPrimaryCtaAsync: mutateAsync,
  };
};
